import { CrescLoader } from "./LoadingScreen";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, RefreshCw, Check, Calendar, Globe, MapPin, Clock, ChevronDown, ChevronRight, Download, Plug, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";

/**
 * CalendarImportModal — connect & import from Google Calendar + Microsoft/
 * Outlook. Iter37 user spec:
 *   "Import from Google Calendar and Microsoft Outlook/Calendar (all
 *    workspaces/calendars) by connecting calendars and choosing which
 *    calendars to connect.
 *    Import settings: All calendars in the import queue will show, each
 *    as dropdowns, with their sub-items attached … checkbox next to all
 *    so the user can choose what they want to import. Checkbox where the
 *    user chooses to import using the current time zone, default time
 *    zone, or location's time zone."
 *
 * Flow:
 *   1. /authorize → window.open OAuth URL → callback redirects back with
 *      `?integration_connected=google`
 *   2. /status confirms connection
 *   3. /calendars lists user's calendars; user toggles checkboxes
 *   4. Pick target workspace + tz mode + range
 *   5. /import → backend pulls events, dedupes, creates native events
 */

const TZ_MODES = [
  { id: "current",  label: "Use my current time zone",      icon: Clock },
  { id: "default",  label: "Use my default (account) zone", icon: Globe },
  { id: "location", label: "Use my current location's zone", icon: MapPin },
];

const PROVIDERS = [
  {
    id: "google",
    label: "Google Calendar",
    accent: "#4285F4",
    logo: (
      <svg viewBox="0 0 64 64" className="w-5 h-5"><path fill="#fff" d="M14 14h36v36H14z" /><path fill="#4285F4" d="M48 18v32H16V18z M52 14v40H12V14z" /><text x="32" y="40" fontSize="20" fontWeight="700" textAnchor="middle" fill="#4285F4">31</text></svg>
    ) },
  {
    id: "microsoft",
    label: "Microsoft Calendar",
    accent: "#0078D4",
    logo: (
      <svg viewBox="0 0 64 64" className="w-5 h-5"><rect width="28" height="28" x="4" y="4" fill="#F25022" /><rect width="28" height="28" x="32" y="4" fill="#7FBA00" /><rect width="28" height="28" x="4" y="32" fill="#00A4EF" /><rect width="28" height="28" x="32" y="32" fill="#FFB900" /></svg>
    ) },
];

function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
}

async function detectLocationTimezone() {
  // Best-effort: ask geolocation, then reverse-geocode → tz. Avoid the
  // reverse-geocode roundtrip; just expose the user's IANA tz directly,
  // which already mirrors physical location for browsers configured to
  // auto-update from OS. If the user is on vacation with their laptop's
  // OS clock unchanged, this gracefully falls back to their default zone.
  return detectTimezone();
}

export default function CalendarImportModal({ open, onClose }) {
  const { workspaces, current: currentWorkspace } = useWorkspace() || {};
  const [activeProvider, setActiveProvider] = useState("google");
  const [statusByProvider, setStatusByProvider] = useState({}); // { google: {connected, email}, microsoft: {...} }
  const [calendarsByProvider, setCalendarsByProvider] = useState({});
  const [loadingCals, setLoadingCals] = useState(false);
  const [selected, setSelected] = useState(new Set()); // composite ids "provider:calId"
  const [expanded, setExpanded] = useState(new Set([...PROVIDERS.map(p => p.id)]));
  const [tzMode, setTzMode] = useState("current");
  const [locationTz, setLocationTz] = useState(null);
  const [rangeDays, setRangeDays] = useState(60);
  const [targetWsId, setTargetWsId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [stickyError, setStickyError] = useState(null);
  const userTimezone = useMemo(() => detectTimezone(), []);
  const popupRef = useRef(null);

  // Pre-select current workspace.
  useEffect(() => { if (open && currentWorkspace?.workspace_id) setTargetWsId(currentWorkspace.workspace_id); }, [open, currentWorkspace]);

  const refreshStatus = useCallback(async () => {
    const next = {};
    for (const p of PROVIDERS) {
      try {
        const { data } = await api.get(`/integrations/${p.id}/status`);
        next[p.id] = data;
      } catch (err) {
        if (err?.response?.status === 503) {
          next[p.id] = { connected: false, _not_configured: err.response.data?.detail };
        } else {
          next[p.id] = { connected: false };
        }
      }
    }
    setStatusByProvider(next);
  }, []);

  const refreshCalendars = useCallback(async (provider) => {
    setLoadingCals(true);
    try {
      const { data } = await api.get(`/integrations/${provider}/calendars`);
      setCalendarsByProvider((prev) => ({ ...prev, [provider]: data }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "integration_not_configured") {
        setStickyError(detail);
      } else if (err?.response?.status === 401) {
        toast.error("Session expired — please reconnect your calendar.");
      } else if (err?.response?.status === 403) {
        toast.error("Permission denied. Re-connect and grant Calendar access when prompted.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to load calendars");
      }
      setCalendarsByProvider((prev) => ({ ...prev, [provider]: [] }));
    }
    setLoadingCals(false);
  }, []);

  // Initial bootstrap on modal open.
  useEffect(() => {
    if (!open) return;
    refreshStatus();
    detectLocationTimezone().then(setLocationTz);
    // Honor return-from-OAuth deep link.
    const url = new URL(window.location.href);
    const connected = url.searchParams.get("integration_connected");
    if (connected) {
      toast.success(`${PROVIDERS.find((p) => p.id === connected)?.label || connected} connected!`);
      url.searchParams.delete("integration_connected");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash);
    }
    const errored = url.searchParams.get("integration_error");
    if (errored) {
      toast.error(`Connection failed: ${errored}`);
      url.searchParams.delete("integration_error");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash);
    }
  }, [open, refreshStatus]);

  // Auto-load calendars for connected providers.
  useEffect(() => {
    if (!open) return;
    for (const p of PROVIDERS) {
      const s = statusByProvider[p.id];
      if (s?.connected && !calendarsByProvider[p.id]) refreshCalendars(p.id);
    }
  }, [open, statusByProvider, calendarsByProvider, refreshCalendars]);

  const connect = async (provider) => {
    setStickyError(null);
    try {
      const { data } = await api.get(`/integrations/${provider}/authorize`);
      // Open OAuth in a popup so the modal stays mounted; refresh status
      // when the popup reaches our callback URL (or simply closes).
      popupRef.current = window.open(data.authorization_url, "cresc-oauth", "width=540,height=720,noopener=no,noreferrer=no");
      const poll = setInterval(async () => {
        if (!popupRef.current || popupRef.current.closed) {
          clearInterval(poll);
          await refreshStatus();
          // Force calendar reload.
          setCalendarsByProvider((prev) => { const n = { ...prev }; delete n[provider]; return n; });
        }
      }, 600);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "integration_not_configured") {
        setStickyError(d);
      } else {
        toast.error("Failed to start OAuth");
      }
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${PROVIDERS.find((p) => p.id === provider)?.label}?`)) return;
    try {
      await api.delete(`/integrations/${provider}/disconnect`);
      toast.success("Disconnected");
      await refreshStatus();
      setCalendarsByProvider((prev) => { const n = { ...prev }; delete n[provider]; return n; });
      setSelected((cur) => {
        const next = new Set(cur);
        for (const k of Array.from(cur)) if (k.startsWith(provider + ":")) next.delete(k);
        return next;
      });
    } catch { toast.error("Disconnect failed"); }
  };

  const toggleExpand = (key) => setExpanded((cur) => {
    const next = new Set(cur);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleSelect = (key) => setSelected((cur) => {
    const next = new Set(cur);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleSelectAll = (provider) => {
    const cals = calendarsByProvider[provider] || [];
    const allKeys = cals.map((c) => `${provider}:${c.id}`);
    const allOn = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
    setSelected((cur) => {
      const next = new Set(cur);
      for (const k of allKeys) {
        if (allOn) next.delete(k); else next.add(k);
      }
      return next;
    });
  };

  const startImport = async () => {
    if (!targetWsId) { toast.error("Pick a destination workspace"); return; }
    if (selected.size === 0) { toast.error("Select at least one calendar"); return; }
    setImporting(true);
    let totalImported = 0;
    let totalSkipped = 0;
    try {
      // Group selections by provider.
      const byProvider = { google: [], microsoft: [] };
      for (const k of selected) {
        const [prov, calId] = k.split(":");
        if (byProvider[prov]) byProvider[prov].push(calId);
      }
      for (const prov of Object.keys(byProvider)) {
        if (byProvider[prov].length === 0) continue;
        const { data } = await api.post(`/integrations/${prov}/import`, {
          workspace_id: targetWsId,
          calendar_ids: byProvider[prov],
          timezone_mode: tzMode,
          user_timezone: userTimezone,
          location_timezone: locationTz || userTimezone,
          range_days: rangeDays });
        totalImported += data.imported || 0;
        totalSkipped += data.skipped || 0;
      }
      toast.success(`Imported ${totalImported} events${totalSkipped ? ` (${totalSkipped} skipped)` : ""}`);
      window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  if (!open) return null;

  const node = (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="calendar-import-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A2A2A]">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#5A90F5]" />
            <h2 className="text-sm font-semibold text-[#EDEDED]">Import Calendar</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]" data-testid="cal-import-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {stickyError && (
            <div className="rounded border border-[#FF9800]/40 bg-[#FF9800]/10 p-3 text-[11px] text-[#FFB870] flex gap-2" data-testid="cal-import-sticky-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Integration not configured</div>
                <div className="opacity-90 mt-0.5">{stickyError.message}</div>
              </div>
            </div>
          )}

          {/* Provider connect cards + calendar trees */}
          {PROVIDERS.map((p) => {
            const status = statusByProvider[p.id] || {};
            const cals = calendarsByProvider[p.id] || [];
            const isExpanded = expanded.has(p.id);
            const allKeys = cals.map((c) => `${p.id}:${c.id}`);
            const allOn = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
            const someOn = !allOn && allKeys.some((k) => selected.has(k));
            const notConfigured = status._not_configured;
            return (
              <div key={p.id} className="rounded border border-[#2A2A2A] bg-[#141414]" data-testid={`cal-import-provider-${p.id}`}>
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <button
                    onClick={() => toggleExpand(p.id)}
                    className="text-[#A1A1AA] hover:text-[#EDEDED]"
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {p.logo}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-[#EDEDED]">{p.label}</div>
                    <div className="text-[10px] text-[#A1A1AA] truncate">
                      {notConfigured ? "Not configured (admin needs to set up OAuth)"
                        : status.connected ? `Connected as ${status.email || "—"}`
                        : "Not connected"}
                    </div>
                  </div>
                  {status.connected ? (
                    <>
                      {allKeys.length > 0 && (
                        <button
                          onClick={() => toggleSelectAll(p.id)}
                          className="text-[10px] px-2 py-1 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a]"
                          data-testid={`cal-import-toggle-all-${p.id}`}
                        >
                          {allOn ? "Deselect all" : someOn ? "Select all" : "Select all"}
                        </button>
                      )}
                      <button
                        onClick={() => refreshCalendars(p.id)}
                        className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"
                        title="Refresh"
                      ><RefreshCw className={`w-3 h-3 ${loadingCals ? "animate-spin" : ""}`} /></button>
                      <button
                        onClick={() => disconnect(p.id)}
                        className="text-[10px] px-2 py-1 rounded border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        data-testid={`cal-import-disconnect-${p.id}`}
                      >Disconnect</button>
                    </>
                  ) : (
                    <button
                      onClick={() => connect(p.id)}
                      disabled={!!notConfigured}
                      className="text-[10px] px-3 py-1.5 rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: p.accent, color: "#fff" }}
                      data-testid={`cal-import-connect-${p.id}`}
                    >
                      <Plug className="w-3 h-3 inline-block mr-1" />Connect
                    </button>
                  )}
                </div>
                {isExpanded && status.connected && (
                  <div className="border-t border-[#2A2A2A] px-3 py-2 space-y-1">
                    {loadingCals && cals.length === 0 && (
                      <div className="text-[11px] text-[#A1A1AA] flex items-center gap-1.5 py-2">
                        <CrescLoader size={12} /> Loading calendars…
                      </div>
                    )}
                    {!loadingCals && cals.length === 0 && (
                      <div className="text-[11px] text-[#52525B] py-2">No calendars found.</div>
                    )}
                    {cals.map((c) => {
                      const key = `${p.id}:${c.id}`;
                      const on = selected.has(key);
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-[#1a1a1a] ${on ? "bg-[#5A90F5]/10" : ""}`}
                          data-testid={`cal-import-cal-${p.id}-${c.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleSelect(key)}
                            className="accent-[#5A90F5] cursor-pointer"
                            data-testid={`cal-import-cal-checkbox-${p.id}-${c.id}`}
                          />
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ background: c.color || p.accent }}
                          />
                          <span className="flex-1 truncate text-[11px] text-[#EDEDED]">
                            {c.name}{c.primary && <span className="ml-1.5 text-[9px] text-[#5A90F5]">PRIMARY</span>}
                          </span>
                          {c.timezone && <span className="text-[9px] text-[#52525B]">{c.timezone}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Import settings */}
          <div className="rounded border border-[#2A2A2A] bg-[#141414] p-3 space-y-3">
            <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">Import settings</div>

            <div>
              <div className="text-[11px] text-[#EDEDED] mb-1.5">Destination workspace</div>
              <select
                value={targetWsId || ""}
                onChange={(e) => setTargetWsId(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                data-testid="cal-import-workspace"
              >
                <option value="">— Select a workspace —</option>
                {(workspaces || []).map((w) => (
                  <option key={w.workspace_id} value={w.workspace_id}>{w.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="text-[11px] text-[#EDEDED] mb-1.5">Time zone</div>
              <div className="space-y-1.5">
                {TZ_MODES.map((m) => {
                  const Icon = m.icon;
                  const active = tzMode === m.id;
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${active ? "border-[#5A90F5] bg-[#5A90F5]/10" : "border-[#2A2A2A] hover:border-[#3A3A3A]"}`}
                      data-testid={`cal-import-tz-${m.id}`}
                    >
                      <input
                        type="radio"
                        checked={active}
                        onChange={() => setTzMode(m.id)}
                        className="accent-[#5A90F5]"
                      />
                      <Icon className="w-3 h-3 text-[#A1A1AA]" />
                      <span className="flex-1 text-[11px] text-[#EDEDED]">{m.label}</span>
                      <span className="text-[9px] text-[#52525B] font-mono">
                        {m.id === "location" ? (locationTz || userTimezone) : userTimezone}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-[11px] text-[#EDEDED] mb-1.5">Range</div>
              <select
                value={rangeDays}
                onChange={(e) => setRangeDays(parseInt(e.target.value, 10))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED]"
                data-testid="cal-import-range"
              >
                <option value="30">Next 30 days</option>
                <option value="60">Next 60 days</option>
                <option value="90">Next 90 days</option>
                <option value="180">Next 6 months</option>
                <option value="365">Next year</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#2A2A2A] flex items-center justify-between">
          <div className="text-[10px] text-[#A1A1AA]">
            {selected.size} calendar{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded text-[#A1A1AA] hover:bg-[#1a1a1a]"
              data-testid="cal-import-cancel"
            >Cancel</button>
            <button
              onClick={startImport}
              disabled={importing || selected.size === 0 || !targetWsId}
              className="text-xs px-4 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="cal-import-start"
            >
              {importing ? <><CrescLoader size={12} /> Importing…</> : <><Download className="w-3 h-3" /> Import</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
